import { useEffect, useRef } from 'react'
import { getRandomFromArray } from 'utils'
import { useWorld } from './use-world'
import { buildQuestDialog } from '../lib/quest-dialog'
import { GRID_SIZE, DIALOG_ACTION_SHOP, XP_NPC_DIALOG } from '../config/constants'

export const useNpcDialog = ({ cfg, positionId, dialogs, approachDialogsById }) => {
  const {
    spritesRef, dialog, setDialog, addSprite, openShop, closeShop, gainXp,
    quests, questProgress, startQuest, completeQuestStep,
    inventory, removeFromInventory, dungeonSlug, heldItemId,
  } = useWorld()

  const pausedRef            = useRef(false)
  const activeDialogRef      = useRef(null)
  const wasApproachDialogRef = useRef(false)
  const playerLeftZoneRef    = useRef(true)
  const dialogDismissPosRef  = useRef(null)
  const questActionRef       = useRef(null)
  const gaveXpRef            = useRef(false)
  const shopOpenRef          = useRef(false)
  const dialogRef            = useRef(dialog)
  useEffect(() => { dialogRef.current = dialog }, [dialog])

  const handleDialogFrame = (sp) => {
    if (sp?.interacting && !pausedRef.current) {
      if (dialogDismissPosRef.current) {
        const playerNow = spritesRef.current?.player?.position
        if (playerNow) {
          const dx = Math.abs(playerNow.x - dialogDismissPosRef.current.x) / GRID_SIZE
          const dy = Math.abs(playerNow.y - dialogDismissPosRef.current.y) / GRID_SIZE
          if (Math.max(dx, dy) < 5) {
            addSprite({ ...sp, interacting: false })
            return
          }
          dialogDismissPosRef.current = null
        }
      }

      const isApproach = !!sp?.approachDialog
      wasApproachDialogRef.current = isApproach
      const pool   = isApproach ? (approachDialogsById[sp.areaId] ?? dialogs) : dialogs
      const chosen = pool.length > 1 ? getRandomFromArray(pool) : pool[0]
      activeDialogRef.current = chosen
      questActionRef.current = null

      let questLines = null
      const questResult = buildQuestDialog({
        quests, questProgress, inventory, heldItemId,
        npcId: positionId, dungeonSlug,
      })
      if (questResult) {
        if (questResult.startQuestId) startQuest(questResult.startQuestId)
        questActionRef.current = questResult.questAction
        questLines = questResult.lines
      }

      const lines = questLines ?? (chosen?.lines?.length
        ? chosen.lines
        : [`${cfg.name || positionId}: Hello, traveler!`])
      pausedRef.current = true
      if (!gaveXpRef.current) {
        gainXp(XP_NPC_DIALOG)
        gaveXpRef.current = true
      }
      setDialog(lines)
      addSprite({ ...sp, approachDialog: false })
    } else if (pausedRef.current && dialogRef.current.length === 0) {
      const sp2 = spritesRef.current[positionId]?.position
      if (sp2) addSprite({ ...sp2, interacting: false })
      if (questActionRef.current?.type === 'deliver') {
        completeQuestStep(questActionRef.current.questId, questActionRef.current.stepId)
        removeFromInventory(questActionRef.current.itemId)
      }
      questActionRef.current = null
      if (wasApproachDialogRef.current) {
        playerLeftZoneRef.current = false
        if (activeDialogRef.current?.action === DIALOG_ACTION_SHOP && cfg.inventory?.length) {
          openShop(cfg.inventory)
          shopOpenRef.current = true
        }
        wasApproachDialogRef.current = false
      }
      const playerOnDismiss = spritesRef.current?.player?.position
      if (playerOnDismiss) dialogDismissPosRef.current = { x: playerOnDismiss.x, y: playerOnDismiss.y }
      pausedRef.current = false
    }
  }

  const closeShopOnLeave = () => {
    if (shopOpenRef.current) {
      closeShop()
      shopOpenRef.current = false
    }
  }

  return { pausedRef, playerLeftZoneRef, shopOpenRef, handleDialogFrame, closeShopOnLeave }
}
